import { Injectable } from '@angular/core';
import { AbstractControl, FormGroup, ValidatorFn } from '@angular/forms';

/**
 * FormValidationService
 *
 * Provides custom validators and validation utilities for registration forms.
 * Ensures consistent validation across all registration components.
 */
@Injectable({
  providedIn: 'root',
})
export class FormValidationService {
  /**
   * Validator for phone numbers (US format: 10 or 11 digits)
   * 
   * @returns ValidatorFn that accepts 10-digit or 11-digit phone numbers (strips non-digits)
   */
  phoneValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) {
        return null; // Don't validate empty values, use required validator for that
      }

      // Remove all non-digit characters for validation
      const phoneNumber = control.value.replace(/\D/g, '');

      // US phone numbers should be 10 digits
      if (phoneNumber.length === 10 || phoneNumber.length === 11) {
        return null; // Valid
      }

      return { invalidPhone: { value: control.value } };
    };
  }

  /**
   * Validator for job titles (2-100 chars, letters/numbers/common punctuation only)
   * 
   * @returns ValidatorFn that rejects too short/long or invalid character sets
   */
  jobTitleValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) {
        return null;
      }

      const jobTitle = control.value.trim();

      // Job title should be at least 2 characters and not too long
      if (jobTitle.length < 2 || jobTitle.length > 100) {
        return { invalidJobTitle: { value: control.value } };
      }

      // Should not contain only numbers or special characters
      if (!/^[a-zA-Z0-9\s\-&/.,()]+$/.test(jobTitle)) {
        return { invalidJobTitle: { value: control.value } };
      }

      return null;
    };
  }

  /**
   * Validator for company names (2-200 chars, any characters allowed)
   * 
   * @returns ValidatorFn that rejects too short/long company names
   */
  companyNameValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) {
        return null;
      }

      const companyName = control.value.trim();

      // Company name should be reasonable length
      if (companyName.length < 2 || companyName.length > 200) {
        return { invalidCompanyName: { value: control.value } };
      }

      return null;
    };
  }

  /**
   * Validator for street addresses (5-200 chars)
   * 
   * @returns ValidatorFn that rejects too short/long addresses
   */
  addressValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) {
        return null;
      }

      const address = control.value.trim();

      // Address should be reasonable length and contain some basic structure
      if (address.length < 5 || address.length > 200) {
        return { invalidAddress: { value: control.value } };
      }

      return null;
    };
  }

  /**
   * Validator for postal codes (supports US ZIP, UK, and Canadian formats)
   * 
   * @returns ValidatorFn that accepts US (12345 or 12345-6789), UK (SW1A 1AA), or Canadian (K1A 0A6) formats
   */
  postalCodeValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) {
        return null;
      }

      const postalCode = control.value.trim();

      // US ZIP: 12345 or 12345-6789
      const usZipPattern = /^\d{5}(-\d{4})?$/;

      // UK Postal Code: SW1A 1AA
      const ukPostalPattern = /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i;

      // Canadian Postal: K1A 0A6
      const canadianPostalPattern = /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/i;

      if (
        usZipPattern.test(postalCode) ||
        ukPostalPattern.test(postalCode) ||
        canadianPostalPattern.test(postalCode)
      ) {
        return null;
      }

      return { invalidPostalCode: { value: control.value } };
    };
  }

  /**
   * Marks all fields in form group as touched to display validation errors
   * Recursively handles nested FormGroups
   * 
   * @param formGroup - FormGroup to mark touched
   */
  markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  /**
   * Generates user-friendly error message for form field based on validation errors
   * 
   * @param control - Form control to check for errors
   * @param fieldName - Human-readable field name for error message
   * @returns Error message string or null if no errors
   */
  getFieldErrorMessage(
    control: AbstractControl | null,
    fieldName: string,
  ): string | null {
    if (!control || !control.errors || !control.touched) {
      return null;
    }

    const errors = control.errors;

    if (errors['required']) {
      return `${fieldName} is required`;
    }

    if (errors['email']) {
      return `Please enter a valid email address`;
    }

    if (errors['minlength']) {
      return `${fieldName} must be at least ${errors['minlength'].requiredLength} characters`;
    }

    if (errors['maxlength']) {
      return `${fieldName} cannot exceed ${errors['maxlength'].requiredLength} characters`;
    }

    if (errors['pattern']) {
      return `${fieldName} format is invalid`;
    }

    if (errors['invalidPhone']) {
      return `Please enter a valid phone number`;
    }

    if (errors['invalidJobTitle']) {
      return `Please enter a valid job title`;
    }

    if (errors['invalidCompanyName']) {
      return `Please enter a valid company name`;
    }

    if (errors['invalidAddress']) {
      return `Please enter a valid address`;
    }

    if (errors['invalidPostalCode']) {
      return `Please enter a valid postal code`;
    }

    return `${fieldName} is invalid`;
  }

  /**
   * Checks if form field has validation errors and should display error styling
   * 
   * @param control - Form control to check
   * @returns Boolean indicating if field is invalid and touched/dirty
   */
  hasFieldError(control: AbstractControl | null): boolean {
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  /**
   * Generates Bootstrap CSS classes for form field based on validation state
   * 
   * @param control - Form control to check
   * @returns CSS class string ('is-valid', 'is-invalid', or empty)
   */
  getFieldCssClasses(control: AbstractControl | null): string {
    if (!control) {
      return '';
    }

    const classes: string[] = [];

    if (control.valid && (control.dirty || control.touched)) {
      classes.push('is-valid');
    }

    if (control.invalid && (control.dirty || control.touched)) {
      classes.push('is-invalid');
    }

    return classes.join(' ');
  }

  /**
   * Validates entire form and returns first error message found
   * Marks all fields as touched to display errors
   * 
   * @param form - FormGroup to validate
   * @param fieldLabels - Map of field names to human-readable labels (optional)
   * @returns First error message or null if form is valid
   */
  getFirstFormError(
    form: FormGroup,
    fieldLabels: Record<string, string> = {},
  ): string | null {
    this.markFormGroupTouched(form);

    for (const key of Object.keys(form.controls)) {
      const control = form.controls[key];
      const label = fieldLabels[key] || key;
      const errorMessage = this.getFieldErrorMessage(control, label);

      if (errorMessage) {
        return errorMessage;
      }
    }

    return null;
  }

  /**
   * Validates form and returns boolean indicating validity
   * Note: Error message should be displayed by caller using RegistrationErrorService
   * 
   * @param form - FormGroup to validate
   * @param fieldLabels - Map of field names to human-readable labels (optional)
   * @returns Boolean indicating if form is valid
   */
  validateFormFields(
    form: FormGroup,
    fieldLabels: Record<string, string> = {},
  ): boolean {
    if (form.valid) {
      return true;
    }

    const errorMessage = this.getFirstFormError(form, fieldLabels);
    if (errorMessage) {
      // Note: This method should be used with RegistrationErrorService
      // The error will be shown by the component using this service
      return false;
    }

    return true;
  }
}
